import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import { X } from 'lucide-react';
import { useState } from 'react';

interface AnnouncementData {
  id: string;
  is_active: boolean;
  text_fr: string;
  text_en: string;
  text_es: string;
  background_color: string;
  text_color: string;
}

export const AnnouncementBanner = () => {
  const { language } = useLanguage();
  const [isDismissed, setIsDismissed] = useState(() => {
    return localStorage.getItem('announcement-dismissed') === 'true';
  });

  const { data: announcement } = useQuery({
    queryKey: ['announcement-banner'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('announcement_banner' as any)
        .select('*')
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        console.error('Error fetching announcement:', error);
        return null;
      }

      return data ? (data as unknown as AnnouncementData) : null;
    },
    refetchInterval: 60000, // Refresh every minute
  });

  const handleDismiss = () => {
    setIsDismissed(true);
    localStorage.setItem('announcement-dismissed', 'true');
  };

  // Reset dismissed state when announcement changes
  if (announcement?.id && localStorage.getItem('announcement-dismissed') === 'true') {
    const dismissedId = localStorage.getItem('announcement-id');
    if (dismissedId !== announcement.id) {
      setIsDismissed(false);
      localStorage.removeItem('announcement-dismissed');
      localStorage.setItem('announcement-id', announcement.id);
    }
  }

  if (!announcement || !announcement.is_active || isDismissed) {
    return null;
  }

  const getText = () => {
    switch (language) {
      case 'en':
        return announcement.text_en;
      case 'es':
        return announcement.text_es;
      default:
        return announcement.text_fr;
    }
  };

  const text = getText();
  if (!text) return null;

  return (
    <div
      className="relative w-full overflow-hidden py-3 px-4"
      style={{
        backgroundColor: announcement.background_color || '#1e293b',
        color: announcement.text_color || '#ffffff',
      }}
    >
      <div className="container mx-auto flex items-center justify-center gap-4">
        <div className="flex-1 flex justify-center">
          <div className="animate-marquee whitespace-nowrap inline-block">
            <span className="text-sm font-medium px-4">{text}</span>
            <span className="text-sm font-medium px-4">{text}</span>
            <span className="text-sm font-medium px-4">{text}</span>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 p-1 rounded-full hover:bg-white/10 transition-colors"
          aria-label="Fermer l'annonce"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
